import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  TextField,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  TrendingUp as TrendingIcon,
  Language as WebsiteIcon,
  Campaign as CampaignIcon,
  CheckCircle as CompleteIcon,
  Pending as PendingIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { campaignApi } from '../../services/campaignApi';

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(3),
}));

const StatBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
}));

const campaignGoalLabels = {
  'product_launch': 'Product/Service Launch',
  'brand_awareness': 'Brand Awareness',
  'event_promotion': 'Event Promotion',
  'sales_promotion': 'Sales/Promotion',
  'content_promotion': 'Newsletter/Content'
};

const CampaignHeader = ({ 
  campaign, 
  totalPosts, 
  selectedPosts, 
  onScheduleSelected,
  onCampaignUpdate,
  isDemoMode = false 
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(campaign?.name || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready':
        return <PendingIcon />;
      case 'active':
        return <TrendingIcon />;
      case 'completed':
        return <CompleteIcon />;
      default:
        return <CampaignIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'warning';
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleEditName = () => {
    setIsEditingName(true);
    setEditedName(campaign.name);
    setUpdateError(null);
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName(campaign.name);
    setUpdateError(null);
  };

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      setUpdateError('Campaign name cannot be empty');
      return;
    }

    if (editedName.trim() === campaign.name) {
      setIsEditingName(false);
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const response = await campaignApi.updateCampaign(campaign.id, {
        name: editedName.trim()
      });

      if (response.success) {
        // Update the local campaign data
        if (onCampaignUpdate) {
          onCampaignUpdate({ ...campaign, name: editedName.trim() });
        }
        setIsEditingName(false);
      } else {
        throw new Error(response.error || 'Failed to update campaign name');
      }
    } catch (error) {
      console.error('Error updating campaign name:', error);
      setUpdateError(error.message || 'Failed to update campaign name');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSaveName();
    } else if (event.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <StyledCard>
      <CardContent>
        {updateError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {updateError}
          </Alert>
        )}
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'inherit',
                  mr: 2
                }}
              >
                {getStatusIcon(campaign.status)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                {isEditingName ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={handleKeyPress}
                      variant="standard"
                      fullWidth
                      autoFocus
                      sx={{
                        '& .MuiInputBase-root': {
                          color: 'inherit',
                          fontSize: '2rem',
                          fontWeight: 'bold'
                        },
                        '& .MuiInput-underline:before': {
                          borderBottomColor: 'rgba(255, 255, 255, 0.42)'
                        },
                        '& .MuiInput-underline:hover:before': {
                          borderBottomColor: 'rgba(255, 255, 255, 0.87)'
                        },
                        '& .MuiInput-underline:after': {
                          borderBottomColor: 'white'
                        }
                      }}
                      disabled={isUpdating}
                    />
                    <IconButton 
                      onClick={handleSaveName} 
                      disabled={isUpdating || !editedName.trim()}
                      sx={{ color: 'inherit' }}
                      title="Save changes"
                    >
                      {isUpdating ? (
                        <CircularProgress size={20} sx={{ color: 'inherit' }} />
                      ) : (
                        <SaveIcon />
                      )}
                    </IconButton>
                    <IconButton 
                      onClick={handleCancelEdit} 
                      disabled={isUpdating}
                      sx={{ color: 'inherit' }}
                      title="Cancel editing"
                    >
                      <CancelIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h4" sx={{ flex: 1 }}>
                      {campaign.name}
                    </Typography>
                    {!isDemoMode && (
                      <IconButton 
                        onClick={handleEditName}
                        sx={{ 
                          color: 'inherit',
                          opacity: 0.8,
                          '&:hover': { opacity: 1 }
                        }}
                        title="Edit campaign name"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {isDemoMode && (
                    <Chip
                      label="DEMO CAMPAIGN"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(255, 215, 0, 0.9)',
                        color: 'rgba(0, 0, 0, 0.87)',
                        fontWeight: 'bold',
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  <Chip
                    label={campaignGoalLabels[campaign.campaignGoal] || campaign.campaignGoal}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'inherit'
                    }}
                  />
                  <Chip
                    label={campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    size="small"
                    color={getStatusColor(campaign.status)}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <WebsiteIcon fontSize="small" />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {campaign.websiteUrl}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Created {formatDate(campaign.createdAt)}
            </Typography>

            {campaign.description && (
              <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
                {campaign.description}
              </Typography>
            )}
            
            {isDemoMode && (
              <Typography variant="body2" sx={{ mt: 2, opacity: 0.8, fontStyle: 'italic' }}>
                This is a sample campaign showcasing SoloSocial's campaign management capabilities.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <StatBox>
                  <Typography variant="h3" fontWeight="bold">
                    {totalPosts}
                  </Typography>
                  <Typography variant="caption">
                    Total Posts
                  </Typography>
                </StatBox>
              </Grid>
              <Grid item xs={4}>
                <StatBox>
                  <Typography variant="h3" fontWeight="bold">
                    {campaign.scheduledPosts || 0}
                  </Typography>
                  <Typography variant="caption">
                    Scheduled
                  </Typography>
                </StatBox>
              </Grid>
              <Grid item xs={4}>
                <StatBox>
                  <Typography variant="h3" fontWeight="bold">
                    {campaign.publishedPosts || 0}
                  </Typography>
                  <Typography variant="caption">
                    Published
                  </Typography>
                </StatBox>
              </Grid>
            </Grid>

            {selectedPosts > 0 && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    {selectedPosts} post{selectedPosts !== 1 ? 's' : ''} selected
                  </Typography>
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={<ScheduleIcon />}
                    onClick={onScheduleSelected}
                    sx={{
                      bgcolor: theme => theme.palette.secondary.main,
                      color: theme => theme.palette.secondary.contrastText,
                      fontWeight: 700,
                      px: 3,
                      boxShadow: theme => theme.customShadows?.button || theme.shadows[2],
                      '&:hover': {
                        bgcolor: theme => theme.palette.secondary.dark,
                        boxShadow: theme => theme.customShadows?.buttonHover || theme.shadows[4],
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Schedule Selected
                  </Button>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default CampaignHeader; 